import { PushNotificationPreview } from "@/components/broadcast/PushNotificationPreview"
import { MessengerPreview } from "@/components/broadcast/MessengerPreview"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { apiRequest, type ApiError } from "@/lib/api"
import { cn } from "@/lib/utils"
import type { Workflow, WorkflowProgress, WorkflowRun as WorkflowRunType } from "@/types"
import type { MessageContent } from "@/types/workflow"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Eye,
  Loader2,
  MessageSquare,
  MousePointer,
  Pause,
  Play,
  RotateCw,
  Send,
  Settings2,
  Trash2,
  Users
} from "lucide-react"
import { Link, useNavigate, useParams } from "react-router-dom"

type WorkflowResponse = {
  workflow: Workflow
}

type RunResponse = {
  workflow: Workflow
  run: WorkflowRunType
}

type ProgressResponse = {
  progress: WorkflowProgress
}

const statusVariant: Record<string, "default" | "secondary" | "destructive" | "outline-solid"> = {
  draft: "outline-solid",
  active: "default",
  running: "default",
  paused: "secondary",
  completed: "secondary",
  failed: "destructive",
}

const formatDateTime = (value: string | null) => {
  if (!value) return "\u2014"
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return "\u2014"
  return parsed.toLocaleString("pt-BR")
}

const formatStatus = (status: string) => {
  const map: Record<string, string> = {
    running: "em execução",
    completed: "concluído",
    failed: "falhou",
    paused: "pausado",
    draft: "rascunho",
    active: "ativo",
  }
  return map[status] ?? status
}

const formatBroadcastStatus = (status: string) => {
  const map: Record<string, string> = {
    running: "em execução",
    completed: "concluído",
    failed: "falhou",
    paused: "pausado",
    draft: "rascunho",
    active: "ativo",
    queued: "na fila",
    sending: "enviando",
    sent: "enviados",
    delivered: "entregues",
    opened: "abertos",
    clicked: "cliques",
  }
  return map[status] ?? status
}

/** Extract message contents from workflow definition for a given node (message node).
 * Mirrors BroadcastJob#build_push_payload: uses contents, templateBodyText, or push fields. */
function getMessageContents(workflow: Workflow | undefined, nodeId: string): MessageContent[] {
  const def = workflow?.definition
  if (!def || typeof def !== "object") return []
  const nodes = (def as Record<string, unknown>).nodes
  if (!nodes || typeof nodes !== "object") return []
  const node = Array.isArray(nodes)
    ? (nodes as Array<Record<string, unknown>>).find((n) => n?.id === nodeId)
    : (nodes as Record<string, Record<string, unknown>>)[nodeId]
  if (!node || typeof node !== "object") return []
  const data = (node.data ?? node.config ?? {}) as Record<string, unknown>
  const nodeType = (node.type as string | undefined) ?? ""

  // 1. Rich contents (text/image/card blocks)
  const contents = data.contents
  if (Array.isArray(contents) && contents.length > 0) return contents as MessageContent[]

  // 2. Template mode: body text
  const templateBodyText = (data.templateBodyText ?? data.template_body_text) as string | undefined
  if (templateBodyText) {
    return [{ id: "template", type: "text", text: templateBodyText }]
  }

  const templateName = (data.templateName ?? data.template_name) as string | undefined
  if (nodeType === "facebook_message" || nodeType === "send_facebook_message") {
    const templateParams = (data.templateParams ?? data.template_params) as string | string[] | undefined
    const normalizedParams = Array.isArray(templateParams)
      ? templateParams.map(String)
      : typeof templateParams === "string"
      ? templateParams.split(/[\n,]/).map((value) => value.trim()).filter(Boolean)
      : []

    const parts = [templateName ? `Template: ${templateName}` : "Template do Messenger"]
    if (normalizedParams.length > 0) {
      parts.push(`Parâmetros: ${normalizedParams.join(", ")}`)
    }

    return [{ id: "facebook-template", type: "text", text: parts.join("\n") }]
  }

  // 3. Push fields (pushTitle, pushDescription, pushImageUrl, etc.) - what BroadcastJob actually sends
  const pushTitle = (data.pushTitle ?? data.push_title ?? data.title) as string | undefined
  const pushDescription = (data.pushDescription ?? data.push_description ?? data.description) as string | undefined
  const pushImageUrl = (data.pushImageUrl ?? data.push_image_url ?? data.imageUrl ?? data.image) as string | undefined
  const pushUrl = (data.pushUrl ?? data.push_url ?? data.url ?? data.link) as string | undefined
  const rawButtons = (data.buttons ?? []) as Array<{ id?: string; title?: string; url?: string }>

  const hasPushContent = pushTitle || pushDescription || pushImageUrl || pushUrl || rawButtons?.length
  if (!hasPushContent) return []

  let buttons = Array.isArray(rawButtons)
    ? rawButtons
        .filter((b) => b && (b.title || b.id))
        .map((b, i) => ({
          id: (b.id ?? `btn-${i}`).toString(),
          type: "url" as const,
          title: (b.title ?? "Link").toString(),
          url: b.url,
        }))
    : []
  // pushUrl becomes the main link when no explicit buttons (BroadcastJob uses it as link)
  if (pushUrl && !buttons.some((b) => b.url)) {
    buttons = [{ id: "link", type: "url" as const, title: "Abrir", url: pushUrl }]
  }

  // Build preview matching what users receive (mirrors BroadcastJob push payload)
  const bodyText = [pushTitle, pushDescription].filter(Boolean).join("\n\n") || "Notificação"
  const hasImage = Boolean(pushImageUrl)
  const hasText = Boolean(pushTitle || pushDescription)

  if (hasImage && hasText) {
    return [{
      id: "push-card",
      type: "card",
      title: pushTitle || undefined,
      subtitle: pushDescription || undefined,
      imageUrl: pushImageUrl,
      buttons: buttons.length ? buttons : undefined,
    }]
  }
  if (hasImage) {
    return [{
      id: "push-card",
      type: "card",
      imageUrl: pushImageUrl,
      buttons: buttons.length ? buttons : undefined,
    }]
  }
  return [{
    id: "push-text",
    type: "text",
    text: bodyText,
    buttons: buttons.length ? buttons : undefined,
  }]
}

const WorkflowRunPage = () => {
  const { projectSlug, workflowId, runId } = useParams<{ projectSlug: string; workflowId: string; runId?: string }>()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const isSpecificRun = Boolean(runId)
  const basePath = `/api/projects/${encodeURIComponent(projectSlug || "")}/workflows/${workflowId}`
  const progressPath = runId ? `${basePath}/progress?run_id=${runId}` : `${basePath}/progress`

  const workflowQuery = useQuery<WorkflowResponse, ApiError>({
    queryKey: ["workflow", projectSlug, workflowId],
    queryFn: () => apiRequest<WorkflowResponse>(basePath),
    enabled: Boolean(projectSlug && workflowId),
  })

  const progressQuery = useQuery<ProgressResponse, ApiError>({
    queryKey: ["workflowProgress", projectSlug, workflowId, runId ?? "latest"],
    queryFn: () => apiRequest<ProgressResponse>(progressPath),
    enabled: Boolean(projectSlug && workflowId),
    refetchInterval: (query) => {
      const progress = query.state.data?.progress
      if (!progress) return 5000
      if (progress.run_status === "completed" || progress.run_status === "failed") return false
      return 5000
    },
  })

  const refreshQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["workflow", projectSlug, workflowId] })
    queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug] })
    queryClient.invalidateQueries({ queryKey: ["workflowProgress", projectSlug, workflowId] })
  }

  const runMutation = useMutation<RunResponse, ApiError>({
    mutationFn: () =>
      apiRequest<RunResponse>(`${basePath}/run`, { method: "POST" }),
    onSuccess: () => refreshQueries(),
  })

  const pauseMutation = useMutation<RunResponse, ApiError>({
    mutationFn: () =>
      apiRequest<RunResponse>(`${basePath}/pause`, { method: "POST" }),
    onSuccess: () => refreshQueries(),
  })

  const resumeMutation = useMutation<RunResponse, ApiError>({
    mutationFn: () =>
      apiRequest<RunResponse>(`${basePath}/resume`, { method: "POST" }),
    onSuccess: () => refreshQueries(),
  })

  const deleteMutation = useMutation<void, ApiError, void>({
    mutationFn: () =>
      apiRequest(basePath, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug] })
      navigate(`/${projectSlug}/broadcasts`)
    },
  })

  const handleDelete = () => {
    const name = workflow?.name ?? "este broadcast"
    const confirmed = window.confirm(
      `Excluir broadcast "${name}"?\n\nEsta ação não pode ser desfeita.`
    )
    if (confirmed) deleteMutation.mutate()
  }

  const workflow = workflowQuery.data?.workflow
  const progress = progressQuery.data?.progress
  const latestRun = workflow?.latest_run
  const isRunning = progress?.run_status === "running"
  const isPaused = progress?.run_status === "paused"
  const hasDefinition = Boolean(
    workflow?.definition && typeof workflow.definition === "object" && "start" in workflow.definition
  )

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "running":
        return <Loader2 className="h-5 w-5 animate-spin" />
      case "completed":
        return <CheckCircle2 className="h-5 w-5" />
      case "failed":
        return <AlertCircle className="h-5 w-5" />
      case "paused":
        return <Pause className="h-5 w-5" />
      default:
        return <Clock className="h-5 w-5" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "running":
        return "bg-blue-50 border-blue-200 text-blue-700"
      case "completed":
        return "bg-green-50 border-green-200 text-green-700"
      case "failed":
        return "bg-red-50 border-red-200 text-red-700"
      case "paused":
        return "bg-yellow-50 border-yellow-200 text-yellow-700"
      default:
        return "bg-gray-50 border-gray-200 text-gray-700"
    }
  }

  const totalMessages = progress?.broadcasts?.reduce((sum, b) => sum + b.total_messages, 0) ?? 0
  const sentMessages = progress?.broadcasts?.reduce((sum, b) => sum + b.sent_count, 0) ?? 0
  const failedMessages = progress?.broadcasts?.reduce((sum, b) => sum + b.failed_count, 0) ?? 0
  const deliveredMessages = progress?.broadcasts?.reduce((sum, b) => sum + b.messages.delivered, 0) ?? 0
  const openedMessages = progress?.broadcasts?.reduce((sum, b) => sum + b.messages.opened, 0) ?? 0
  const clickedMessages = progress?.broadcasts?.reduce((sum, b) => sum + b.messages.clicked, 0) ?? 0
  const estimatedRecipients = workflow?.estimated_recipients ?? 0
  const hasRunBefore = Boolean(latestRun?.started_at || progress?.started_at)

  return (
    <div className="flex flex-col gap-6 pb-8">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to={isSpecificRun ? `/${projectSlug}/broadcasts/${workflowId}/recurring` : `/${projectSlug}/broadcasts`}>
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{workflow?.name ?? "Execução do Broadcast"}</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {isSpecificRun
                ? "Análises detalhadas do disparo"
                : "Monitore e controle a execução do broadcast"}
            </p>
          </div>
        </div>
        {!isSpecificRun && (
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to={`/${projectSlug}/broadcasts/${workflowId}/builder`}>
                <Settings2 className="mr-2 h-4 w-4" />
                Editar Broadcast
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              disabled={deleteMutation.isPending || !workflow}
              onClick={handleDelete}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        )}
      </div>

      {workflowQuery.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : null}

      {workflowQuery.isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">
            {workflowQuery.error?.message ?? "Falha ao carregar broadcast."}
          </p>
        </div>
      ) : null}

      {workflow ? (
        <>
          {/* Status Overview Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
            {/* Workflow Status */}
            <div className={cn(
              "rounded-lg border-2 p-4 transition-all",
              getStatusColor(progress?.run_status ?? "draft")
            )}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide opacity-70">Status</p>
                  <p className="mt-1 text-2xl font-bold capitalize">
                    {formatStatus(progress?.run_status ?? workflow.status)}
                  </p>
                </div>
                {getStatusIcon(progress?.run_status ?? workflow.status)}
              </div>
            </div>

            {/* Estimated Audience */}
            <div className="rounded-lg border-2 bg-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Audiência</p>
                  <p className="mt-1 text-2xl font-bold">{estimatedRecipients.toLocaleString()}</p>
                </div>
                <Users className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>

            {/* Total Messages */}
            <div className="rounded-lg border-2 bg-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Mensagens</p>
                  <p className="mt-1 text-2xl font-bold">{totalMessages.toLocaleString()}</p>
                </div>
                <MessageSquare className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>

            {/* Sent Messages */}
            <div className="rounded-lg border-2 bg-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Enviados</p>
                  <p className="mt-1 text-2xl font-bold text-blue-600">
                    {sentMessages.toLocaleString()}
                  </p>
                  {totalMessages > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {((sentMessages / totalMessages) * 100).toFixed(1)}%
                    </p>
                  )}
                </div>
                <Send className="h-5 w-5 text-blue-600" />
              </div>
            </div>

            {/* Delivered Messages */}
            <div className="rounded-lg border-2 bg-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Entregues</p>
                  <p className="mt-1 text-2xl font-bold text-green-600">
                    {deliveredMessages.toLocaleString()}
                  </p>
                  {failedMessages > 0 && (
                    <p className="text-xs text-red-600">
                      {failedMessages} falharam
                    </p>
                  )}
                </div>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
            </div>

            {/* Opened Messages */}
            <div className="rounded-lg border-2 bg-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Abertos</p>
                  <p className="mt-1 text-2xl font-bold text-amber-600">
                    {openedMessages.toLocaleString()}
                  </p>
                </div>
                <Eye className="h-5 w-5 text-amber-600" />
              </div>
            </div>

            {/* Clicked Messages */}
            <div className="rounded-lg border-2 bg-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Cliques</p>
                  <p className="mt-1 text-2xl font-bold text-indigo-600">
                    {clickedMessages.toLocaleString()}
                  </p>
                </div>
                <MousePointer className="h-5 w-5 text-indigo-600" />
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className={cn("grid gap-6", isSpecificRun ? "" : "lg:grid-cols-3")}>
            {/* Left Column - Workflow Info & Controls */}
            {!isSpecificRun && <div className="space-y-6 lg:col-span-1">
              {/* Workflow Info Card */}
              <div className="rounded-lg border bg-card p-5">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">
                  Informações do Broadcast
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Nó Atual</p>
                    <p className="mt-0.5 text-sm font-mono bg-muted px-2 py-1 rounded">
                      {progress?.current_node_id ?? "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Iniciado em</p>
                    <p className="mt-0.5 text-sm">
                      {formatDateTime(progress?.started_at ?? latestRun?.started_at ?? null)}
                    </p>
                  </div>
                  {progress?.completed_at && (
                    <div>
                      <p className="text-xs text-muted-foreground">Concluído em</p>
                      <p className="mt-0.5 text-sm">
                        {formatDateTime(progress.completed_at)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Control Panel */}
              <div className="rounded-lg border bg-card p-5">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">
                  Controles
                </h3>

                <div className="mb-4 rounded-md border bg-muted/30 px-3 py-2">
                  <p className="text-xs text-muted-foreground">Audiência estimada antes da execução</p>
                  <p className="text-base font-semibold">{estimatedRecipients.toLocaleString()}</p>
                </div>

                {!hasDefinition ? (
                  <p className="text-sm text-muted-foreground mb-4">
                    Este broadcast ainda não tem definição. Crie-o no construtor.
                  </p>
                ) : null}

                <div className="space-y-2">
                  <Button
                    type="button"
                    className="w-full"
                    size="lg"
                    disabled={!hasDefinition || hasRunBefore || isRunning || runMutation.isPending}
                    onClick={() => runMutation.mutate()}
                  >
                    {runMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Iniciando...
                      </>
                    ) : isRunning ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Em execução
                      </>
                    ) : (
                      <>
                        <Play className="mr-2 h-4 w-4" />
                        Executar Broadcast
                      </>
                    )}
                  </Button>

                  {hasRunBefore && !isRunning && !isPaused ? (
                    <p className="text-xs text-muted-foreground">
                      Este broadcast já foi enviado uma vez e não pode ser executado novamente.
                    </p>
                  ) : null}

                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full"
                    size="lg"
                    disabled={!isRunning || pauseMutation.isPending}
                    onClick={() => pauseMutation.mutate()}
                  >
                    {pauseMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Pausando...
                      </>
                    ) : (
                      <>
                        <Pause className="mr-2 h-4 w-4" />
                        Pausar
                      </>
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    size="lg"
                    disabled={!isPaused || resumeMutation.isPending}
                    onClick={() => resumeMutation.mutate()}
                  >
                    {resumeMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Retomando...
                      </>
                    ) : (
                      <>
                        <RotateCw className="mr-2 h-4 w-4" />
                        Retomar
                      </>
                    )}
                  </Button>
                </div>

                {/* Error Messages */}
                {runMutation.isError && (
                  <p className="mt-3 text-sm text-destructive">{runMutation.error.message}</p>
                )}
                {pauseMutation.isError && (
                  <p className="mt-3 text-sm text-destructive">{pauseMutation.error.message}</p>
                )}
                {resumeMutation.isError && (
                  <p className="mt-3 text-sm text-destructive">{resumeMutation.error.message}</p>
                )}
              </div>
            </div>}

            {/* Right Column - Broadcast Details */}
            <div className={cn("space-y-4", isSpecificRun ? "" : "lg:col-span-2")}>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Mensagens do Broadcast
              </h3>

              {progressQuery.isError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <p className="text-sm text-red-800">
                    {progressQuery.error?.message ?? "Falha ao buscar progresso."}
                  </p>
                </div>
              )}

              {progress?.broadcasts && progress.broadcasts.length > 0 ? (
                <div className="space-y-4">
                  {progress.broadcasts.map((broadcast, index) => {
                    const percentage = broadcast.total_messages > 0
                      ? ((broadcast.sent_count + broadcast.failed_count) / broadcast.total_messages) * 100
                      : 0

                    return (
                      <div
                        key={broadcast.node_id}
                        className="rounded-lg border-2 bg-card overflow-hidden transition-all hover:border-primary/50"
                      >
                        {/* Header */}
                        <div className="bg-muted/30 px-5 py-3 border-b">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-semibold">
                                {index + 1}
                              </div>
                              <div>
                                <p className="font-medium text-sm">Nó de Broadcast</p>
                                <p className="text-xs text-muted-foreground font-mono">
                                  {broadcast.node_id}
                                </p>
                              </div>
                            </div>
                            <Badge 
                              variant={statusVariant[broadcast.status] ?? "outline-solid"}
                              className="text-xs font-semibold"
                            >
                              {formatBroadcastStatus(broadcast.status)}
                            </Badge>
                          </div>
                        </div>

                        {/* Progress Stats */}
                        <div className="px-5 py-4 space-y-4">
                          {/* Progress Bar */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium">Progresso</span>
                              <span className="text-sm font-semibold">
                                {percentage.toFixed(1)}%
                              </span>
                            </div>
                            <div className="h-3 w-full rounded-full bg-muted overflow-hidden">
                              <div
                                className="h-full bg-linear-to-r from-blue-500 to-blue-600 transition-all duration-500 ease-out rounded-full"
                                style={{ width: `${Math.min(100, percentage)}%` }}
                              />
                            </div>
                          </div>

                          {/* Stats Grid */}
                          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
                            <div className="text-center p-2 rounded-lg bg-muted/50">
                              <p className="text-xs text-muted-foreground">Na fila</p>
                              <p className="text-lg font-bold mt-0.5">{broadcast.messages.queued}</p>
                            </div>
                            <div className="text-center p-2 rounded-lg bg-blue-50">
                              <p className="text-xs text-blue-600">Enviando</p>
                              <p className="text-lg font-bold text-blue-700 mt-0.5">{broadcast.messages.sending}</p>
                            </div>
                            <div className="text-center p-2 rounded-lg bg-purple-50">
                              <p className="text-xs text-purple-600">Enviados</p>
                              <p className="text-lg font-bold text-purple-700 mt-0.5">{broadcast.messages.sent}</p>
                            </div>
                            <div className="text-center p-2 rounded-lg bg-green-50">
                              <p className="text-xs text-green-600">Entregues</p>
                              <p className="text-lg font-bold text-green-700 mt-0.5">{broadcast.messages.delivered}</p>
                            </div>
                            <div className="text-center p-2 rounded-lg bg-red-50">
                              <p className="text-xs text-red-600">Falharam</p>
                              <p className="text-lg font-bold text-red-700 mt-0.5">{broadcast.messages.failed}</p>
                            </div>
                            <div className="text-center p-2 rounded-lg bg-amber-50">
                              <p className="text-xs text-amber-600">Abertos</p>
                              <p className="text-lg font-bold text-amber-700 mt-0.5">{broadcast.messages.opened}</p>
                            </div>
                            <div className="text-center p-2 rounded-lg bg-indigo-50">
                              <p className="text-xs text-indigo-600">Cliques</p>
                              <p className="text-lg font-bold text-indigo-700 mt-0.5">{broadcast.messages.clicked}</p>
                            </div>
                            <div className="text-center p-2 rounded-lg bg-sky-50">
                              <p className="text-xs text-sky-600">Eventos de Clique</p>
                              <p className="text-lg font-bold text-sky-700 mt-0.5">{broadcast.messages.click_events}</p>
                            </div>
                          </div>

                          {/* Message Preview */}
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                              Pré-visualização da Mensagem
                            </p>
                            {workflow?.channel === "facebook_message" ? (
                              <MessengerPreview
                                contents={getMessageContents(workflow, broadcast.node_id)}
                                compact
                              />
                            ) : (
                              <PushNotificationPreview
                                contents={getMessageContents(workflow, broadcast.node_id)}
                                siteName={workflow?.name}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="rounded-lg border-2 border-dashed bg-muted/20 p-8 text-center">
                  <MessageSquare className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <p className="mt-3 text-sm font-medium text-muted-foreground">
                    Nenhum broadcast ainda
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Os broadcasts aparecerão aqui quando o broadcast for executado
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}

export default WorkflowRunPage
